import { useCallback, useContext, useEffect, useState } from 'react';

import { FeatureFlagManager } from './FeatureFlagManager';
import { FeatureFlagContext } from './FeatureFlagProvider';
import { FeatureFlagState } from './types';

/**
 * Hook to access feature flag context
 */
export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagContext must be used within a FeatureFlagProvider',
    );
  }
  return context;
};

/**
 * Hook to check if a specific feature flag is enabled
 */
export const useFeatureFlag = (flagKey: string): boolean => {
  const { flags, isEnabled } = useFeatureFlagContext();
  return flags[flagKey] ?? isEnabled(flagKey);
};

/**
 * Hook to get all feature flags
 */
export const useFeatureFlags = (): FeatureFlagState => {
  const { flags } = useFeatureFlagContext();
  return flags;
};

/**
 * Hook to get feature flag controls (get, set, reset)
 */
export const useFeatureFlagControls = () => {
  const context = useFeatureFlagContext();
  return {
    isEnabled: context.isEnabled,
    getFlag: context.getFlag,
    setFlag: context.setFlag,
    resetFlag: context.resetFlag,
    resetAllFlags: context.resetAllFlags,
  };
};

/**
 * Hook to get multiple feature flags at once
 */
export const useFeatureFlagsMultiple = (
  flagKeys: string[],
): Record<string, boolean> => {
  const { flags } = useFeatureFlagContext();

  return flagKeys.reduce(
    (acc, key) => {
      acc[key] = flags[key] ?? false;
      return acc;
    },
    {} as Record<string, boolean>,
  );
};

/**
 * Hook to subscribe to feature flag changes without context
 * Useful for components outside the provider tree
 */
export const useFeatureFlagSubscription = (
  flagKey?: string,
): FeatureFlagState => {
  const [flags, setFlags] = useState<FeatureFlagState>({});

  useEffect(() => {
    const manager = FeatureFlagManager.getInstance();

    // Initialize with current flags
    setFlags(manager.getAllFlags());

    // Subscribe to changes
    return manager.subscribe((newFlags) => {
      setFlags(newFlags);
    });
  }, []);

  // If specific flag requested, return just that flag's state
  if (flagKey) {
    return { [flagKey]: flags[flagKey] ?? false };
  }

  return flags;
};

/**
 * Hook to toggle a feature flag
 */
export const useFeatureFlagToggle = (flagKey: string) => {
  const { setFlag } = useFeatureFlagControls();
  const isEnabled = useFeatureFlag(flagKey);

  const toggle = useCallback(() => {
    setFlag(flagKey, !isEnabled);
  }, [flagKey, isEnabled, setFlag]);

  return {
    isEnabled,
    toggle,
    enable: () => setFlag(flagKey, true),
    disable: () => setFlag(flagKey, false),
  };
};

/**
 * Hook for conditional rendering based on feature flag
 */
export const useFeatureFlagRender = (flagKey: string) => {
  const isEnabled = useFeatureFlag(flagKey);

  const renderIf = useCallback(
    (component: React.ReactNode) => {
      return isEnabled ? component : null;
    },
    [isEnabled],
  );

  const renderUnless = useCallback(
    (component: React.ReactNode) => {
      return !isEnabled ? component : null;
    },
    [isEnabled],
  );

  return {
    isEnabled,
    renderIf,
    renderUnless,
  };
};

/**
 * Hook to get feature flags by category
 */

export const useFeatureFlagsByCategory = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  category: string,
): Record<string, boolean> => {
  const flags = useFeatureFlags();

  // This would need the FEATURE_FLAGS config to filter by category
  // For now, return all flags (you can enhance this based on your needs)
  return flags;
};

/**
 * Hook for debugging feature flags in development
 */
export const useFeatureFlagDebug = () => {
  const flags = useFeatureFlags();
  const controls = useFeatureFlagControls();

  return {
    flags,
    controls,
    logFlags: () => console.table(flags),
    exportFlags: () => JSON.stringify(flags, null, 2),
  };
};
